import { useState, lazy, Suspense, memo } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ParallaxHeroBg } from "@/components/ui/parallax-hero-bg";
// Optimized responsive images - properly sized for display dimensions
// LCP image served from public/ for HTML-level discoverability (preload in index.html)
const excelloFinishedRedRoof = "/images/excello-finished-red-roof-compressed.webp";
import excelloFinishedStairs from "@/assets/excello-finished-stairs-compressed.webp";
import excelloConstruction1 from "@/assets/excello-construction-1-compressed.webp";
import excelloConstruction2 from "@/assets/excello-construction-2-compressed.webp";
import { Home, Palette, Eye, ChevronDown, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

// Lazy load animation components - only needed when visible
const ScrollReveal = lazy(() => import("@/components/ui/scroll-reveal").then(m => ({ default: m.ScrollReveal })));
const ParallaxImage = lazy(() => import("@/components/ui/parallax").then(m => ({ default: m.ParallaxImage })));
const DeferredContent = lazy(() => import("@/components/ui/deferred-content").then(m => ({ default: m.DeferredContent })));

// Lazy load dialog components - only needed on user interaction
const Dialog = lazy(() => import("@/components/ui/dialog").then(m => ({ default: m.Dialog })));
const DialogContent = lazy(() => import("@/components/ui/dialog").then(m => ({ default: m.DialogContent })));
const DialogDescription = lazy(() => import("@/components/ui/dialog").then(m => ({ default: m.DialogDescription })));
const DialogHeader = lazy(() => import("@/components/ui/dialog").then(m => ({ default: m.DialogHeader })));
const DialogTitle = lazy(() => import("@/components/ui/dialog").then(m => ({ default: m.DialogTitle })));

export const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);

  const handleStartJourney = () => {
    setShowJourneyDialog(true);
  };

  return (
    <>
      {/* Journey Choice Dialog - lazy loaded */}
      {showJourneyDialog && (
        <Suspense fallback={null}>
          <Dialog open={showJourneyDialog} onOpenChange={setShowJourneyDialog}>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-2xl text-center">Start Your Journey</DialogTitle>
                <DialogDescription className="text-center">
                  Get started with your personalized project estimate
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                {/* Project Cost Estimate */}
                <button
                  onClick={() => {
                    setShowJourneyDialog(false);
                    navigate("/track-status");
                  }}
                  className="group p-6 rounded-xl border-2 border-border hover:border-secondary bg-card hover:bg-secondary/5 transition-all duration-300 text-left"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-secondary/10 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
                      <FileText className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1 text-foreground group-hover:text-secondary transition-colors">
                        Request a Project Cost Estimate
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Submit your own plans or project details to receive a personalized cost estimate
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </Suspense>
      )}

      <Suspense fallback={
        <div className="min-h-screen">
          <section className="relative min-h-[90vh] flex items-center justify-center pt-20 pb-8 md:pb-12 overflow-hidden">
            <div className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
              <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-7xl mx-auto">
                <div className="space-y-6 md:space-y-8 text-center lg:text-left order-1 lg:order-1 min-h-[280px] sm:min-h-[320px] md:min-h-[360px]" />
                <div className="order-2 lg:order-2 min-h-[500px] lg:min-h-[600px]" />
              </div>
            </div>
          </section>
        </div>
      }>
      <div className="min-h-screen">
      {/* Hero Section with Image Gallery */}
      <section className="relative min-h-[90vh] flex items-center justify-center pt-20 pb-8 md:pb-12 overflow-hidden">
        {/* Parallax Background */}
        <ParallaxHeroBg />
        <div className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center max-w-7xl mx-auto">
            {/* Text Content - min-height prevents CLS */}
            <div className="space-y-6 md:space-y-8 text-center lg:text-left order-1 lg:order-1 min-h-[280px] sm:min-h-[320px] md:min-h-[360px]">
              <ScrollReveal animation="fade-in" duration={800}>
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight gradient-text">
                  {t('hero.title')}
                </h1>
              </ScrollReveal>
              <ScrollReveal animation="fade-in" delay={150} duration={800}>
                <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground leading-relaxed">
                  {t('hero.subtitle')}
                </p>
              </ScrollReveal>
              <ScrollReveal animation="fade-in" delay={300} duration={800}>
                <div className="flex justify-center lg:justify-start">
                  <Button
                    size="lg"
                    onClick={handleStartJourney}
                    className="text-base sm:text-lg px-8 sm:px-12 py-5 sm:py-6 h-auto font-semibold w-full sm:w-auto hover-lift hover-glow pulse-glow group relative overflow-hidden bg-secondary text-secondary-foreground hover:bg-secondary/90"
                  >
                    <span className="relative z-10 transition-transform duration-300 group-hover:scale-110 inline-block">
                      {t('hero.cta')}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/80 to-destructive opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </Button>
                </div>
              </ScrollReveal>
            </div>

            {/* Image Gallery Grid - fixed dimensions to prevent CLS */}
            <div className="order-2 lg:order-2 min-h-[500px] lg:min-h-[600px]">
              <div className="grid grid-cols-2 gap-3">
                {/* Featured large image - LCP element - NOT lazy loaded */}
                <ScrollReveal animation="slide-in-right" delay={100} className="col-span-2">
                  <div className="rounded-xl overflow-hidden shadow-brand border border-secondary/10 hover:border-secondary/30 transition-all duration-300 hover-lift group aspect-[3/2]">
                    <img
                      src={excelloFinishedRedRoof}
                      sizes="(max-width: 768px) 100vw, 584px"
                      alt="Byrdson Services - Beautiful elevated home with red roof"
                      loading="eager"
                      // @ts-ignore - fetchpriority is valid HTML attribute
                      fetchpriority="high"
                      decoding="async"
                      width={584}
                      height={389}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                </ScrollReveal>
                {/* Two medium images - middle row - lazy loaded */}
                <ScrollReveal animation="scale-in" delay={250}>
                  <div className="rounded-lg overflow-hidden shadow-medium border border-border hover:shadow-brand hover:border-secondary/20 transition-all duration-300 hover-lift group aspect-[3/2]">
                    <img
                      src={excelloFinishedStairs}
                      sizes="(max-width: 768px) 50vw, 284px"
                      alt="Byrdson Services - Finished home with elevated foundation and stairs"
                      loading="lazy"
                      decoding="async"
                      width={284}
                      height={190}
                      className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-transform duration-700"
                    />
                  </div>
                </ScrollReveal>
                <ScrollReveal animation="scale-in" delay={350}>
                  <div className="rounded-lg overflow-hidden shadow-medium border border-border hover:shadow-brand hover:border-secondary/20 transition-all duration-300 hover-lift group aspect-[3/2]">
                    <img
                      src={excelloConstruction1}
                      sizes="(max-width: 768px) 50vw, 284px"
                      alt="Byrdson Services - Construction process showing elevated foundation"
                      loading="lazy"
                      decoding="async"
                      width={284}
                      height={190}
                      className="w-full h-full object-cover group-hover:scale-110 group-hover:-rotate-1 transition-transform duration-700"
                    />
                  </div>
                </ScrollReveal>
                {/* Bottom row - single construction image - lazy loaded */}
                <ScrollReveal animation="slide-up" delay={450} className="col-span-2">
                  <div className="rounded-lg overflow-hidden shadow-soft border border-border hover:shadow-medium hover:border-secondary/20 transition-all duration-300 hover-lift group aspect-[3/1]">
                    <img
                      src={excelloConstruction2}
                      sizes="(max-width: 768px) 100vw, 584px"
                      alt="Byrdson Services - Elevated construction foundation system"
                      loading="lazy"
                      decoding="async"
                      width={584}
                      height={259}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Down Indicator */}
        <button 
          onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in cursor-pointer group transition-transform hover:scale-110" 
          style={{ animationDelay: '1s' }}
          aria-label="Scroll to next section"
        >
          <span className="text-xs font-medium text-muted-foreground/70 uppercase tracking-widest group-hover:text-secondary transition-colors">Scroll</span>
          <div className="relative">
            <ChevronDown 
              className="w-6 h-6 text-secondary animate-bounce group-hover:text-primary transition-colors" 
              style={{ animationDuration: '2s' }}
            />
            <ChevronDown 
              className="w-6 h-6 text-secondary/40 absolute top-2 left-0 animate-bounce group-hover:text-primary/40 transition-colors" 
              style={{ animationDuration: '2s', animationDelay: '0.15s' }}
            />
          </div>
        </button>
      </section>

      {/* Below-the-fold content - deferred loading */}
      <DeferredContent 
        rootMargin="300px" 
        minHeight="600px"
        fallback={<div className="py-12 sm:py-16 md:py-20 bg-muted/30" />}
      >
        {/* About Byrdson Services */}
        <section id="about-section" className="py-12 sm:py-16 md:py-20 bg-muted/30 scroll-mt-20 content-auto">
          <div className="container mx-auto px-4 sm:px-6">
            <ScrollReveal animation="slide-up" duration={800}>
              <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground">
                  About Byrdson Services
                </h2>
                <ScrollReveal animation="fade-in" delay={200}>
                  <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
                    Byrdson Services is committed to building resilient, high-quality homes that meet the highest standards of construction excellence. We specialize in creating energy-efficient, hurricane-resistant homes that comply with Florida Building Code and the Elevate Florida program requirements.
                  </p>
                </ScrollReveal>
                <ScrollReveal animation="fade-in" delay={350}>
                  <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed">
                    Our mission is to provide homeowners with beautiful, durable homes while offering an innovative digital experience that puts you in control of your home's design from the very beginning.
                  </p>
                </ScrollReveal>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* What This Platform Does */}
        <section className="py-12 sm:py-16 md:py-20 content-auto">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <ScrollReveal animation="slide-down" duration={800}>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground text-center mb-8 sm:mb-12 md:mb-16">
                  {t('hero.featuresTitle')}
                </h2>
              </ScrollReveal>
              
              <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
                {/* Feature 1 */}
                <ScrollReveal animation="bounce-in" delay={100} duration={600}>
                  <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border transition-all duration-300 hover-lift interactive-card group h-full">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-secondary/10 rounded-full flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse-glow">
                      <Home className="w-6 h-6 sm:w-8 sm:h-8 text-secondary group-hover:text-secondary-foreground transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-foreground group-hover:text-secondary transition-colors duration-300">
                      {t('hero.feature1Title')}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {t('hero.feature1Desc')}
                    </p>
                  </div>
                </ScrollReveal>

                {/* Feature 2 */}
                <ScrollReveal animation="bounce-in" delay={250} duration={600}>
                  <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border transition-all duration-300 hover-lift interactive-card group h-full">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-secondary/10 rounded-full flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse-glow">
                      <Palette className="w-6 h-6 sm:w-8 sm:h-8 text-secondary group-hover:text-secondary-foreground transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-foreground group-hover:text-secondary transition-colors duration-300">
                      {t('hero.feature2Title')}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {t('hero.feature2Desc')}
                    </p>
                  </div>
                </ScrollReveal>

                {/* Feature 3 */}
                <ScrollReveal animation="bounce-in" delay={400} duration={600}>
                  <div className="text-center space-y-3 sm:space-y-4 p-4 sm:p-6 rounded-lg bg-card border border-border transition-all duration-300 hover-lift interactive-card group h-full">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-secondary/10 rounded-full flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 group-hover:scale-110 group-hover:animate-pulse-glow">
                      <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-secondary group-hover:text-secondary-foreground transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-semibold text-foreground group-hover:text-secondary transition-colors duration-300">
                      {t('hero.feature3Title')}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground">
                      {t('hero.feature3Desc')}
                    </p>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>
      </DeferredContent>
      </div>
      </Suspense>
    </>
  );
};
